import type { PlatformAdapter } from '@cogo/runtime-v2'
import {
  hashHostCapabilityContent,
} from '@cogo/schema/host-capability-manifest'
import type { ContentRef } from '@cogo/publisher-core'
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaders,
} from '@/services/cogo-platform-config'

const PLATFORM_BASE = getCogoPlatformBase()

export function getStudioRuntimeApiBase(): string | undefined {
  const trimmed = PLATFORM_BASE.trim().replace(/\/$/, '')
  return trimmed.length > 0 ? trimmed : undefined
}

function rewriteRelativeRuntimeUrl(input: string, apiBase: string): string {
  if (input.startsWith('/')) return `${apiBase}${input}`
  if (input.startsWith('./')) return `${apiBase}/${input.slice(2)}`
  if (input.startsWith('../')) return `${apiBase}/${input.slice(3)}`
  return input
}

const STUDIO_RUNTIME_NETWORK_ADAPTER_DESCRIPTOR = {
  id: 'cogo.studio.runtime-network.v1',
  implementation: 'apps/cogo-studio/src/shell/ide-v2/host/studio-runtime-network.ts',
  operations: ['fetch', 'fetch-with-headers'],
} as const

export const STUDIO_RUNTIME_NETWORK_ADAPTER_REF = {
  ref: 'cogo://studio/host-adapter/runtime-network/v1',
  contentHash: hashHostCapabilityContent(STUDIO_RUNTIME_NETWORK_ADAPTER_DESCRIPTOR),
} as const

export interface StudioRuntimePlatformBundle {
  platform: PlatformAdapter
  capabilityBindings: readonly {
    capabilityId: `host.${string}`
    operation: string
    adapterRef: ContentRef
    sideEffectClass: 'none' | 'reversible' | 'external'
    implementation: (...args: never[]) => unknown
  }[]
}

export function createStudioRuntimePlatform(
  suppliedApiBase?: string,
): StudioRuntimePlatformBundle | undefined {
  const apiBase = suppliedApiBase ?? getStudioRuntimeApiBase()
  if (!apiBase) return undefined

  const fetchImplementation: NonNullable<PlatformAdapter['fetch']> = (input, init) =>
    globalThis.fetch(rewriteRelativeRuntimeUrl(String(input), apiBase), {
      ...init,
      headers: withCogoDevProxyHeaders(init?.headers),
    })
  const platform = {
    fetch: fetchImplementation,
  } as Partial<PlatformAdapter> as PlatformAdapter

  return {
    platform,
    capabilityBindings: [
      {
        capabilityId: 'host.fetch',
        operation: 'fetch',
        adapterRef: STUDIO_RUNTIME_NETWORK_ADAPTER_REF,
        sideEffectClass: 'external',
        implementation: fetchImplementation as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.fetch',
        operation: 'fetch-with-headers',
        adapterRef: STUDIO_RUNTIME_NETWORK_ADAPTER_REF,
        sideEffectClass: 'external',
        implementation: fetchImplementation as (...args: never[]) => unknown,
      },
    ],
  }
}
